import { Doc } from '@dadohr/shared/domainTypes/document';
import { OktaIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/okta';
import { SAMLIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/saml';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { SsoProviderType } from '@dadohr/shared/domainTypes/sso';
import { CF } from '@dadohr/shared/versions';
import {
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  styled,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useState } from 'react';
import * as ENVS from '../../../../../../env.json';

import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DataFieldWithLabel } from '../../../../../../components/DataFieldWithLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { enableSso } from '../../../../../../services/orgs';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties
} from '../common';
import { removeSAMLConfig, useSAMLConfig } from './service';

const NAME = 'SAML SSO';

const SUPPORT = 'help@dadohr.com';

const URLS = {
  acs: ENVS.saml.generic.acs,
  entityId: ENVS.saml.generic.entityId,
  nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  open,
  onClose,
  config
}: {
  config: Doc<SAMLIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  const [state, setState] = useState<{ idpMetadata: string }>({
    idpMetadata: ''
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>

      <Content>
        <Alert severity="info" icon={false}>
          {t(
            'Integrate with your Identity Provider through SAML 2.0 to allow Single-Sign-On for all your employees.'
          )}
        </Alert>

        <Typography component="p">
          Please configure your Identity Provider with the following values.
          Afterwards copy your application's XML metadata into the textarea
          below.
        </Typography>

        <Typography component="p">
          If you need further assistance with setting up your IdP, feel free to
          get in touch with us at{' '}
          <LinkExternal color="primary" href={`mailto:${SUPPORT}`}>
            {SUPPORT}
          </LinkExternal>
          .
        </Typography>

        <DataFieldWithLabel label="ACS URL" copyToClipboardText={URLS.acs}>
          {URLS.acs}
        </DataFieldWithLabel>
        <DataFieldWithLabel
          label="Entity ID"
          copyToClipboardText={URLS.entityId}
        >
          {URLS.entityId}
        </DataFieldWithLabel>
        <DataFieldWithLabel
          label="Name ID Format"
          copyToClipboardText={URLS.nameIdFormat}
        >
          {URLS.nameIdFormat}
        </DataFieldWithLabel>
        <br />

        <FormRow>
          <CustomInputLabel label={'IDP XML Metadata'} fullWidth>
            <TextField
              variant="outlined"
              value={state.idpMetadata}
              multiline
              rows={8}
              onChange={(ev) =>
                setState((s) => ({ ...s, idpMetadata: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>

      <DialogActionsWithSlots
        variant="normal"
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(CF.saml.verifyCredentialsAndStore, {
                  orgId: config.data.orgId,
                  idpMetadata: state.idpMetadata
                })
              }
              disabled={!state.idpMetadata}
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const ExistingSetupDialog = ({
  open,
  onClose,
  org,
  config
}: {
  open: boolean;
  onClose: () => void;
  org: IOrganization;
  config: Doc<OktaIntegrationConfig>;
}) => {
  // Say that it's set up. Maybe add a verify button later.
  // No need to show any of the provided xml
  // Add switch to activate/deactivate SSO for the org in general
  const { t } = useUnsafeTranslation();

  const enabledForEmployees =
    org.sso.enabledForEndUsers && org.sso.provider === SsoProviderType.SAML;
  const enabledForAdmins =
    org.sso.enabledForAdmins && org.sso.provider === SsoProviderType.SAML;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert
          severity="success"
          icon={false}
          action={
            <ButtonWithPromise
              variant="contained"
              color="primary"
              size="small"
              pending={t('Testing...')}
              onClick={() => {
                return callFirebaseFunction<{ loginUrl: string }>(
                  'adminUser-testSsoLogin',
                  {
                    provider: SsoProviderType.SAML,
                    orgId: config.data.orgId
                  }
                ).then(({ loginUrl }) => {
                  console.log(loginUrl);
                  window.open(loginUrl, '__blank');
                });
              }}
            >
              {t('Test login')}
            </ButtonWithPromise>
          }
        >
          {t('Successfully integrated via SAML2.0')}
        </Alert>

        <FormControlLabel
          label={t('Enable Single-Sign On for End Users')}
          control={
            <Switch
              checked={enabledForEmployees}
              onChange={async () => {
                await enableSso(org.id, {
                  enabledForEndUsers: !enabledForEmployees,
                  enabledForAdmins: enabledForAdmins,
                  provider: SsoProviderType.SAML
                });
              }}
              color="primary"
            />
          }
        />

        <FormControlLabel
          label={t('Enable Single-Sign On for Admin Users')}
          control={
            <Switch
              checked={enabledForAdmins}
              onChange={async () => {
                await enableSso(org.id, {
                  enabledForEndUsers: enabledForEmployees,
                  enabledForAdmins: !enabledForAdmins,
                  provider: SsoProviderType.SAML
                });
              }}
              color="primary"
            />
          }
        />
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await enableSso(org.id, {
                enabledForAdmins: false,
                enabledForEndUsers: false
              });
              await removeSAMLConfig(org.id);
            }}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const SetupDialog = ({
  config,
  onClose,
  onSuccessfulIntegration: onSucessfulIntegration, // can be ignored since this integration is not an import source
  open
}: IntegrationSetupDialogWithConfigProperties<SAMLIntegrationConfig>) => {
  const setup = !!config?.data.idpMetadata;
  const { org } = useCurrentUser();

  return (
    <>
      {config && setup && (
        <ExistingSetupDialog
          org={org}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
      {config && !setup && (
        <InitialSetupDialog config={config} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const SamlSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();

  const [config, , error] = useSAMLConfig(org.id, userId);
  useErrorLogger('SAML Config error', error);
  const cfg = config || null;
  return (
    <>
      <IntegrationConfigurationButton
        functionalities={functionalities}
        name={NAME}
        logoUrl="/images/integrations-logos/saml-logo.png"
        active={!!cfg?.data.active}
        interactive={interactive}
        renderSetupDialog={({
          open,
          onClose,
          onFailure,
          onSuccessfulIntegration
        }: IntegrationSetupDialogProperties) => (
          <SetupDialog
            config={cfg}
            open={open}
            onClose={onClose}
            onFailure={onFailure}
            onSuccessfulIntegration={onSuccessfulIntegration}
          />
        )}
      />
    </>
  );
};
